<template>
  <div class="w-full">
    <div class="flex w-full flex-col">
      <div
        v-if="hasImportantMessage"
        class="bg-black p-3 text-center text-2xl text-white"
      >
        <h2 v-if="importantMessageHeadline" class="font-medium">
          {{ importantMessageHeadline }}
        </h2>
        <div v-if="importantMessageText">{{ importantMessageText }}</div>
      </div>
      <StoreFinderMap class="hidden md:inline-block md:h-[300px]" />
      <StoreDetailSkeleton v-if="loading || !store" />
      <StoreDetails v-else :store="store" :globals="data" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMapMarker } from '~/composables/useMapMarker'
import { useStore } from '~/composables/useStores'
import {
  generateBreadcrumbSchema,
  getUrl,
  metaTagGenerator,
  type IBreadcrumbItem,
} from '~/utils/seo'
import type { IStore } from '~/rpcMethods'
import {
  getStoreImportantMsgText,
  getStoreImportantMsgHeadline,
  getStoreMetaTitle,
  getStoreMetaDescription,
} from '~/utils/store'

const route = useRoute()
const router = useRouter()
const scope = effectScope()
onScopeDispose(() => scope.stop())

const { id, city } = route.params
const { $fimLocalePath, $i18n } = useNuxtApp()
const $currentShop = useCurrentShop()
const slug = computed(() => `${city}/${id}`)
const { data: store, fetching: fetchingStore } = await useStore({
  params: { storeId: slug.value },
  key: `useStore-${slug.value}`,
})
const { resetMarkers, addMarker, resetLatLngBounds } = useMapMarker()

const { data, pending: fetchingGlobals } = useCmsStory({
  params: {
    slug: 'store-details-global',
    baseFolder: CmsBaseFolder.Global,
  },
  key: `NiederlassungenStoreDetailsPage-${slug.value}`,
})

const importantMessageHeadline = computed(
  () => store.value && getStoreImportantMsgHeadline(store?.value),
)
const importantMessageText = computed(
  () => store.value && getStoreImportantMsgText(store?.value),
)

const hasImportantMessage = computed(
  () =>
    store?.value &&
    (getStoreImportantMsgHeadline(store?.value) ||
      getStoreImportantMsgText(store?.value)),
)

const setMarkers = (store: IStore) => {
  addMarker({
    position: {
      lat: store.storeLocation.coordinates['0'],
      lng: store.storeLocation.coordinates['1'],
    },
    store,
    anchor: `store-${store.id}`,
    noClick: true,
    active: true,
  })
  resetLatLngBounds()
}

onBeforeMount(() => resetMarkers())
onBeforeUnmount(() => resetMarkers())
onUpdated(() => {
  if (store.value) {
    setMarkers(store.value)
  } else if (!fetchingStore.value) {
    router.replace($fimLocalePath(routeList.stores))
  }
})
scope.run(() => {
  if (import.meta.server) {
    return
  }
  watch(store, () => {
    if (store.value) {
      setMarkers(store.value)
    }
  })
})

const loading = computed(() => fetchingStore.value || fetchingGlobals.value)

const metaTitle = computed(() => {
  if (!store.value) {
    return ''
  }
  return getStoreMetaTitle(store.value, $i18n)
})

const metaDescription = computed(() => {
  if (!store.value) {
    return ''
  }
  return getStoreMetaDescription(store.value, $i18n)
})

const metaTags = metaTagGenerator({
  description: metaDescription.value,
  robots: 'index,follow',
})

useHead({
  link: [
    {
      rel: 'canonical',
      key: 'canonical',
      href: sanitizeCanonical(getUrl($currentShop.value.domain, route?.path)),
    },
  ],
})

useSeoMeta({
  ...metaTags,
  ...(metaTitle.value ? { title: metaTitle.value } : {}),
  ...metaTags,
})

const breadcrumbSchema = computed((): IBreadcrumbItem[] => {
  return [
    {
      name: $i18n.t('store_finder.stores'),
      item: getUrl(
        $currentShop.value.domain,
        $fimLocalePath({ name: routeList.stores.name }),
      ),
    },
    {
      name: metaTitle.value,
      item: getUrl($currentShop.value.domain, route.path),
    },
  ]
})

useJsonld(() => generateBreadcrumbSchema(breadcrumbSchema.value))

defineOptions({ name: 'NiederlassungenStoreDetailsPage' })
definePageMeta({ pageType: 'store' })
</script>
